/**
 * Online match parameters from the address: `?room=<id>` names the room; the ticket that opens
 * the seat never stays in the URL. The queue hands it over in sessionStorage; a development link
 * may carry it once as `?ticket=` and it is moved out of the address on first sight.
 */
export interface OnlineParams {
  roomId: string;
  ticket: string | null;
  /** The ticket came from the address and should be stored, then removed from it. */
  ticketFromUrl: boolean;
}

const ROOM_ID = /^[A-Za-z0-9_-]{1,64}$/;

export function ticketStorageKey(roomId: string): string {
  return `sw:ticket:${roomId}`;
}

/** Where the reconnection token of a joined room is kept so a reload resumes the seat. */
export function reconnectStorageKey(roomId: string): string {
  return `sw:reconnect:${roomId}`;
}

export function readStored(storage: Storage | null, key: string): string | null {
  try {
    return storage?.getItem(key) ?? null;
  } catch {
    return null;
  }
}

export function writeStored(storage: Storage | null, key: string, value: string | null): void {
  try {
    if (value === null) storage?.removeItem(key);
    else storage?.setItem(key, value);
  } catch {
    // Session storage may be unavailable; the match still works for this page load.
  }
}

export function parseOnlineParams(search: string, storage: Storage | null): OnlineParams | null {
  const q = new URLSearchParams(search);
  const roomId = q.get('room');
  if (roomId === null || !ROOM_ID.test(roomId)) return null;
  const fromUrl = q.get('ticket');
  if (fromUrl !== null && fromUrl.length > 0) {
    return { roomId, ticket: fromUrl, ticketFromUrl: true };
  }
  return { roomId, ticket: readStored(storage, ticketStorageKey(roomId)), ticketFromUrl: false };
}

/** The address without the online-only parameters (`room`, `ticket`). */
export function stripOnlineParams(search: string): string {
  const q = new URLSearchParams(search);
  q.delete('room');
  q.delete('ticket');
  const rest = q.toString();
  return rest.length > 0 ? `?${rest}` : '';
}

/** The address with the ticket removed and everything else (room, quality, test flags) kept. */
export function withoutTicket(search: string): string {
  const q = new URLSearchParams(search);
  q.delete('ticket');
  const rest = q.toString();
  return rest.length > 0 ? `?${rest}` : '';
}
